import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { NavBar } from '../components/NavBar';
import { BackToHome } from '../components/BackToHome';
import { Tx } from '../components/EditMode';
import { Mail, MailX, XCircle, CheckCircle2, LogOut, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AccountPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const action = searchParams.get('action');  // 'unsubscribe' or null
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useI18n();

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [autoActionDone, setAutoActionDone] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchState();
  }, []);

  const fetchState = async () => {
    setLoading(true);
    try {
      const url = token ? `${API}/account/state?token=${encodeURIComponent(token)}` : `${API}/account/state`;
      const { data } = await axios.get(url, { withCredentials: true });
      setState(data);
      // If ?action=unsubscribe in URL and user is currently subscribed, do it immediately
      if (action === 'unsubscribe' && data.email_subscribed && !autoActionDone) {
        await doUnsubscribe(false);
        setAutoActionDone(true);
      }
    } catch (e) {
      setMessage(e?.response?.data?.detail || t('account.error.load'));
    } finally {
      setLoading(false);
    }
  };

  const doUnsubscribe = async (subscribe) => {
    setActing(true);
    setMessage('');
    try {
      await axios.post(`${API}/account/unsubscribe`,
        { token, subscribe },
        { withCredentials: true }
      );
      setState((s) => ({ ...s, email_subscribed: subscribe }));
      setMessage(subscribe ? t('account.success.subscribed') : t('account.success.unsubscribed'));
    } catch (e) {
      setMessage(e?.response?.data?.detail || t('account.error.action'));
    } finally {
      setActing(false);
    }
  };

  const doCancelPledge = async () => {
    setActing(true);
    setMessage('');
    try {
      await axios.post(`${API}/account/cancel-pledge`,
        { token },
        { withCredentials: true }
      );
      setState((s) => ({ ...s, pledge_status: 'inactive' }));
      setMessage(t('account.success.cancelled'));
      setConfirmCancel(false);
    } catch (e) {
      setMessage(e?.response?.data?.detail || t('account.error.action'));
    } finally {
      setActing(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {!token && <NavBar />}
        <div className="max-w-md mx-auto px-4 py-20">
          <div className="bg-white rounded-2xl shadow-md p-8 text-center">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">{t('account.error.title')}</h2>
            <p className="text-slate-600 text-sm">{message || t('account.error.load')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {!token && <NavBar />}
      {!token && <BackToHome />}

      <div className="max-w-xl mx-auto px-4 py-10 sm:py-14">
        <div className="bg-white rounded-2xl shadow-md border border-slate-100 p-8">
          <h1 className="text-3xl font-bold text-slate-900 font-['Outfit'] tracking-tight mb-2">
            {t('account.heading', { name: state.name })}
          </h1>
          <p className="text-sm text-slate-500 mb-8">{state.email}</p>

          {/* Status message */}
          {message && (
            <div data-testid="account-message" className={`mb-6 p-4 rounded-xl text-sm ${
              message.includes('error') || message.includes('Failed')
                ? 'bg-red-50 border border-red-200 text-red-700'
                : 'bg-green-50 border border-green-200 text-green-700'
            }`}>
              {message}
            </div>
          )}

          {/* Email subscription */}
          <section data-testid="account-email-section" className="mb-8 p-5 bg-slate-50 rounded-xl">
            <div className="flex items-start gap-3 mb-3">
              {state.email_subscribed ? (
                <Mail className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              ) : (
                <MailX className="w-6 h-6 text-slate-400 flex-shrink-0 mt-1" />
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900"><Tx k="account.email.heading" /></h3>
                <p className="text-sm text-slate-600 mt-0.5">
                  {state.email_subscribed
                    ? <Tx k="account.email.subscribed_body" />
                    : <Tx k="account.email.unsubscribed_body" />}
                </p>
              </div>
            </div>
            {state.email_subscribed ? (
              <button
                data-testid="account-unsubscribe-btn"
                onClick={() => doUnsubscribe(false)}
                disabled={acting}
                className="px-5 py-2.5 bg-slate-900 text-white rounded-full text-sm font-semibold hover:bg-slate-800 disabled:opacity-50"
              >
                <Tx k="account.email.unsubscribe" />
              </button>
            ) : (
              <button
                data-testid="account-resubscribe-btn"
                onClick={() => doUnsubscribe(true)}
                disabled={acting}
                className="px-5 py-2.5 bg-blue-600 text-white rounded-full text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                <Tx k="account.email.resubscribe" />
              </button>
            )}
          </section>

          {/* Pledge cancellation */}
          {state.has_profile && (
            <section data-testid="account-pledge-section" className="p-5 bg-slate-50 rounded-xl">
              <div className="flex items-start gap-3 mb-3">
                {state.pledge_status === 'inactive' ? (
                  <XCircle className="w-6 h-6 text-slate-400 flex-shrink-0 mt-1" />
                ) : (
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900"><Tx k="account.pledge.heading" /></h3>
                  <p className="text-sm text-slate-600 mt-0.5">
                    {state.pledge_status === 'inactive'
                      ? <Tx k="account.pledge.inactive_body" />
                      : t('account.pledge.active_body', { amount: state.pledge_amount > 0 ? `NOK ${state.pledge_amount.toLocaleString()}` : '' })}
                  </p>
                </div>
              </div>
              {state.pledge_status !== 'inactive' && !confirmCancel && (
                <button
                  data-testid="account-cancel-pledge-btn"
                  onClick={() => setConfirmCancel(true)}
                  className="px-5 py-2.5 bg-red-50 text-red-700 border border-red-200 rounded-full text-sm font-semibold hover:bg-red-100"
                >
                  <Tx k="account.pledge.cancel" />
                </button>
              )}
              {confirmCancel && (
                <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-sm text-red-800 mb-3 font-medium"><Tx k="account.pledge.confirm" /></p>
                  <div className="flex gap-2">
                    <button
                      data-testid="account-cancel-pledge-confirm"
                      onClick={doCancelPledge}
                      disabled={acting}
                      className="px-4 py-2 bg-red-600 text-white rounded-full text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
                    >
                      {acting ? '…' : <Tx k="account.pledge.confirm_yes" />}
                    </button>
                    <button onClick={() => setConfirmCancel(false)}
                            className="px-4 py-2 bg-white text-slate-700 border border-slate-200 rounded-full text-sm font-semibold hover:bg-slate-50">
                      <Tx k="account.pledge.confirm_no" />
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Logout (only if authenticated, not via token) */}
          {!token && user && (
            <button
              data-testid="account-logout-btn"
              onClick={handleLogout}
              className="mt-8 w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-slate-100 text-slate-700 rounded-full text-sm font-medium hover:bg-slate-200"
            >
              <LogOut className="w-4 h-4" /> <Tx k="nav.logout" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
